import { Component, OnInit, signal, computed } from "@angular/core";
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
import { AuthService } from "../../services/auth.service";
import { CashService } from "../../services/cash.service";
import { IncomeRequestsService } from "../../services/income-requests.service";
import { BRAND } from "../../config/branding";

interface NavItem {
  label: string;
  path: string;
  icon: string;
  adminOnly?: boolean;
}
interface NavGroup {
  title: string;
  items: NavItem[];
}

@Component({
  selector: "app-admin-layout",
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet],
  templateUrl: "./admin-layout.component.html"
})
export class AdminLayoutComponent implements OnInit {
  readonly brand = BRAND;
  readonly trustedLogoSmall: any;
  sidebarOpen = false;
  readonly sidebarCollapsed = signal(false);
  readonly pendingCount = signal(0);
  readonly cashPENOpen = signal<boolean | null>(null);
  readonly cashUSDOpen = signal<boolean | null>(null);

  private readonly allGroups: NavGroup[] = [
    { title: "Operación", items: [
      { label: "Dashboard", path: "/dashboard", icon: "dashboard" },
      { label: "Caja", path: "/cash", icon: "cash" },
      { label: "Movimientos", path: "/movements", icon: "movements" },
    ]},
    { title: "Equipo", items: [
      { label: "Nómina", path: "/workers", adminOnly: true, icon: "workers" },
      { label: "Solicitudes", path: "/income-requests", adminOnly: true, icon: "requests" },
    ]},
    { title: "Administración", items: [
      { label: "Categorías", path: "/categories", icon: "categories" },
      { label: "Reportes", path: "/reports", icon: "reports" },
      { label: "Auditoría", path: "/audit", adminOnly: true, icon: "audit" },
      { label: "Usuarios", path: "/users", adminOnly: true, icon: "users" },
      { label: "Permisos", path: "/permissions", adminOnly: true, icon: "permissions" }
    ]},
  ];

  readonly navGroups = computed(() => {
    const isAdmin = this.authService.user()?.role === "admin";
    return this.allGroups.map(g => ({
      title: g.title,
      items: g.items.filter((item) => !item.adminOnly || isAdmin)
    })).filter(g => g.items.length);
  });

  // legacy flat for compat
  readonly navigation = computed(() => this.navGroups().flatMap(g => g.items));

  readonly currentUser = computed(() => this.authService.user());

  readonly userInitials = computed(() => {
    const u = this.currentUser();
    if (!u) return "U";
    return `${(u.firstName?.[0] || "").toUpperCase()}${(u.lastName?.[0] || "").toUpperCase()}`;
  });

  constructor(
    readonly authService: AuthService,
    private readonly cashService: CashService,
    private readonly incomeRequestsService: IncomeRequestsService,
    private readonly router: Router,
    sanitizer: DomSanitizer
  ) {
    this.trustedLogoSmall = sanitizer.bypassSecurityTrustHtml(this.brand.logoSmall);
  }

  ngOnInit(): void {
    if (this.authService.user()?.role === "admin") {
      this.loadPendingCount();
      setInterval(() => this.loadPendingCount(), 60000);
    }
    this.loadCashStatus();
    // refresh caja pills cada 30s
    setInterval(() => this.loadCashStatus(), 30000);
  }

  loadPendingCount(): void {
    this.incomeRequestsService.countPending().subscribe({
      next: (data) => this.pendingCount.set(data.count)
    });
  }

  loadCashStatus(): void {
    this.cashService.getAll().subscribe({
      next: (data: any) => {
        this.cashPENOpen.set(!!data?.PEN?.opening);
        this.cashUSDOpen.set(!!data?.USD?.opening);
      },
      error: () => {
        this.cashService.getCurrent("PEN").subscribe({ next: d => this.cashPENOpen.set(!!(d as any)?.opening) });
        this.cashService.getCurrent("USD").subscribe({ next: d => this.cashUSDOpen.set(!!(d as any)?.opening) });
      }
    });
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((v) => !v);
  }

  logout(): void {
    this.authService.logout();
    this.router.navigateByUrl("/login");
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }
}
